import { z } from 'zod';
/**
 * "From gyms": Steado's review of what gyms add to their own catalogs (Steado
 * for Gyms, D77). A gym's foods and exercises are usable at once in that gym
 * only; this queue is where Steado decides whether each becomes part of the
 * catalog everyone uses. Nothing reaches the shared catalog without a person
 * deciding here.
 *
 * Items are **grouped across gyms by name**, so three gyms adding "Iron Den
 * whey shake" and "whey shake" read as one decision, with every gym's values
 * side by side. Groups are ranked by reach: how many gyms added it, how many
 * members have it in a plan now, and how often it was logged in the last 30
 * days (the INFOODS "key foods" idea: decide what most people eat first).
 *
 * The decisions, each on a whole group:
 * - **Make a catalog food or exercise**: the console's own create form, named
 *   for the group; the new row is then linked here (`link`). The gym rows stay
 *   as they are, so no plan or log changes, and their trainers are told.
 * - **Same as a catalog one**: link the group to an existing catalog row.
 * - **Keep it the gym's own**: recorded with why. A gym that edits it later
 *   puts it back in the queue.
 */
export const GYM_SEARCH_KINDS = ['food', 'exercise'];
/** How a gym has done in review so far: the share of its items Steado adopted. */
const gymTrustSchema = z.object({
    /** Linked to, or made into, a catalog row. */
    adopted: z.number().int(),
    /** Every decided item: adopted and kept the gym's own. */
    decided: z.number().int(),
});
const usageSchema = z.object({
    /** Members with it in the plan they are living in now. */
    membersInPlans: z.number().int(),
    /** Logged in the last 30 days, every member counted. */
    logs30d: z.number().int(),
});
/** One gym's food in a group. Per 100 g, as stored. */
export const adminGymFoodItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    gymId: z.string().uuid(),
    gymName: z.string(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    dietClass: z.string(),
    allergens: z.array(z.string()),
    hasOnionGarlic: z.boolean(),
    jainExcluded: z.boolean(),
    kcalPer100g: z.number(),
    proteinGPer100g: z.number(),
    carbsGPer100g: z.number(),
    fatGPer100g: z.number(),
    fibreGPer100g: z.number().nullable(),
    /** How the gym serves it: its one portion. */
    serving: z.object({ label: z.string(), grams: z.number() }).nullable(),
    usage: usageSchema,
    gymTrust: gymTrustSchema,
});
/** A catalog row by much the same name: "Steado may already have this". */
const similarSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Its name is the group's, letter for letter (after normalising). */
    sameName: z.boolean(),
});
/** The spread of a group's numbers: how far its gyms disagree. */
const spreadSchema = z.object({ kcal: z.number(), proteinG: z.number() });
export const adminGymFoodGroupSchema = z.object({
    /** The normalised name every item in the group shares. */
    key: z.string(),
    /** The most common name as the gyms wrote it. */
    name: z.string(),
    gyms: z.number().int(),
    usage: usageSchema,
    /** Max minus min per 100 g across the group; zero for a group of one. */
    spread: spreadSchema,
    items: z.array(adminGymFoodItemSchema),
    similar: z.array(similarSchema),
});
/** One gym's exercise in a group: the trainer's answers, as given. */
export const adminGymExerciseItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    gymId: z.string().uuid(),
    gymName: z.string(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    category: z.string(),
    level: z.string(),
    bodyAreas: z.array(z.string()),
    position: z.string().nullable(),
    highImpact: z.boolean(),
    trunkFlexion: z.boolean(),
    avoidInPregnancy: z.boolean(),
    equipment: z.array(z.string()),
    primaryMuscles: z.array(z.string()),
    holdSeconds: z.number().nullable(),
    steps: z.array(z.string()),
    /** Its GIF and photos, resolved; empty when it has none or this environment cannot serve them. */
    demoUrl: z.string().nullable(),
    imageUrls: z.array(z.string()),
    usage: usageSchema,
    gymTrust: gymTrustSchema,
});
export const adminGymExerciseGroupSchema = z.object({
    key: z.string(),
    name: z.string(),
    gyms: z.number().int(),
    usage: usageSchema,
    items: z.array(adminGymExerciseItemSchema),
    similar: z.array(similarSchema),
});
/**
 * The most gym rows one link or keep request decides. A group is as large as
 * the number of gyms that added the name, so the console sends a larger one
 * in batches of this many; a decided row is skipped, so a batch sent again
 * after a failure decides only what is left.
 */
export const ADMIN_GYM_DECISION_MAX_IDS = 50;
/** `POST .../foods/link`: the group's gym foods, and the catalog food that now stands for them. */
export const adminGymFoodLinkRequestSchema = z.object({
    gymFoodIds: z.array(z.string().uuid()).min(1).max(ADMIN_GYM_DECISION_MAX_IDS),
    catalogFoodId: z.string().uuid(),
});
export const adminGymExerciseLinkRequestSchema = z.object({
    gymExerciseIds: z.array(z.string().uuid()).min(1).max(ADMIN_GYM_DECISION_MAX_IDS),
    catalogExerciseId: z.string().uuid(),
});
/** "Keep it the gym's own", and why: the reason is stored on each row. */
export const adminGymKeepRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(ADMIN_GYM_DECISION_MAX_IDS),
    /** Shown to the gym's owner as Steado's note on each row (`steadoNote`). */
    note: z.string().trim().min(1).max(500),
});
/** How many rows a decision changed. */
export const adminGymDecisionSchema = z.object({ decided: z.number().int(), pushed: z.number().int() });
// --- Reports --------------------------------------------------------------------------
export const GYM_FOOD_REPORT_STATUSES = ['open', 'resolved', 'rejected'];
export const GYM_FOOD_REPORT_KINDS = ['macros', 'name', 'portion', 'other'];
/** What a gym says is wrong with a catalog food. */
export const adminGymFoodReportSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(GYM_FOOD_REPORT_STATUSES),
    kind: z.enum(GYM_FOOD_REPORT_KINDS),
    note: z.string().nullable(),
    /** Where the gym says the right values come from: a label, a brand, a recipe. */
    source: z.string().nullable(),
    /** The values the gym says are right, per 100 g. */
    proposed: z
        .object({
        kcal: z.number(),
        proteinG: z.number(),
        carbsG: z.number(),
        fatG: z.number(),
        fibreG: z.number().nullable(),
    })
        .nullable(),
    food: z.object({
        id: z.string().uuid(),
        name: z.string(),
        kcalPer100g: z.number(),
        proteinGPer100g: z.number(),
        carbsGPer100g: z.number(),
        fatGPer100g: z.number(),
    }),
    gymName: z.string(),
    reportedByName: z.string().nullable(),
    /** The gym's own corrected copy ("Make a gym version"), when it made one. */
    gymFoodId: z.string().uuid().nullable(),
    resolution: z.string().nullable(),
    createdAt: z.string(),
});
export const adminGymFoodReportListQuerySchema = z.object({
    status: z.enum(GYM_FOOD_REPORT_STATUSES).default('open'),
});
export const adminGymFoodReportParamsSchema = z.object({ id: z.string().uuid() });
/** Answering a report, in words the gym's trainer reads. The fix itself is made on the food's own page. */
export const adminGymFoodReportAnswerRequestSchema = z.object({
    resolution: z.string().trim().min(1).max(500),
});
// --- Searches that found nothing ------------------------------------------------------------
/** What trainers searched for and did not find, across gyms: where the catalog is thin. */
export const adminGymSearchMissSchema = z.object({
    query: z.string(),
    gyms: z.number().int(),
    count: z.number().int(),
    lastAt: z.string(),
});
export const adminGymSearchMissQuerySchema = z.object({
    kind: z.enum(GYM_SEARCH_KINDS).default('food'),
});
//# sourceMappingURL=gym-catalog.js.map